/**
 * TIẾN TRÌNH ARENA — PHẦN ĐƯỢC GHI VÀO SAVE (màn thứ ba, chế độ BỔ SUNG).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ ĐỌC TRƯỚC KHI SỬA: ĐÂY LÀ CHỖ DỄ LÀM MẤT TIẾN TRÌNH NGƯỜI CHƠI NHẤT TRONG CẢ TASK
 * ════════════════════════════════════════════════════════════════════════════════════════════
 * Người dùng thật đang có save **v7** với tiến trình TD (vàng · class đã mở · màn đã qua · kim
 * cương · mảnh · chiêu đã ghép · cấp tập luyện). Arena là chế độ BỔ SUNG — nếu việc thêm tiến
 * trình Arena làm hỏng migrate thì họ MẤT SẠCH tiến trình TD.
 *
 * Luật BẤT DI BẤT DỊCH của repo (xem `migrateMeta` trong `src/save/storage.ts`):
 *   · Save THIẾU field  ⇒ vá thành MẶC ĐỊNH, KHÔNG ném lỗi.
 *   · Save RÁC (sai kiểu/ null/ undefined/ NaN/ mảng nơi cần object) ⇒ KHÔNG ném, trả mặc định an toàn.
 *   · Mọi giá trị TD cũ phải GIỮ NGUYÊN từng cái một.
 *
 * ⚠️ VÌ SAO FILE NÀY LÀ MODULE THUẦN (0 import Phaser, 0 import `world.ts`):
 *   · `src/save/storage.ts` import nó ⇒ nếu nó kéo theo Phaser thì `migrateMeta()` không chạy được
 *     trong môi trường node của vitest, tức KHÔNG TEST ĐƯỢC đúng cái hàm nguy hiểm nhất.
 *   · `tests/arena-meta.test.ts` dựng save v7 giả bằng tay rồi gọi thẳng các hàm ở đây. Muốn vậy
 *     chúng phải chạy được headless. (Bài học có thật trong repo: `arena-battle.ts` import Phaser
 *     nên tầng hình học phải tách sang `arena-camera.ts` mới test được.)
 *
 * ⚠️ VÌ SAO FILE NÀY KHÔNG IMPORT `src/sim/arena/world.ts`: `world` là LUẬT CỦA TRẬN (mô phỏng
 * 30Hz), còn đây là TIẾN TRÌNH VĨNH VIỄN. Trộn hai tầng là mở đường cho tầng save phụ thuộc vào
 * hằng số cân bằng (đổi `ARENA_ECON` là đổi luôn ngữ nghĩa save cũ). Hai chỗ duy nhất cần con số
 * của sim — "số land" và "số mảnh để mở class" — được khai bằng HẰNG SỐ Ở ĐÂY kèm test khoá.
 *
 * ⚠️⚠️ "SỐ LAND" LÀ DI SẢN CỦA **ĐỊNH DẠNG SAVE**, KHÔNG PHẢI CỦA TẦNG SIM — ĐỌC KỸ TRƯỚC KHI XOÁ.
 * `land` đã bị xoá khỏi tầng sim ở lần refactor "xoá 3 land" (`ARENA_LANE_Y`, `land-pick.ts`,
 * `Land`…). Nhưng `ARENA_PROGRESS_LANDS` dưới đây thì KHÔNG được xoá theo, vì nó mô tả **mảng
 * `mineLevels` đã ghi ra ĐĨA**: mỗi save cũ có ĐÚNG 3 phần tử (cấp khai sinh của từng mỏ, theo thứ
 * tự land ngày xưa). Xoá hay đổi số này là **save của người chơi đang chơi không đọc được nữa**.
 * Đây chính là lý do file tầng save khai lại hằng số thay vì import từ sim: định dạng đã đóng băng,
 * còn sim thì vẫn đang đổi.
 * ⚠️ Hệ quả cần biết: tên `ARENA_PROGRESS_LANDS` và `mineLevels` giờ mang nghĩa LỊCH SỬ ("3 ô cấp mỏ
 * trong save"), không còn nghĩa "3 land trong trận". Đổi tên là việc của một migration save riêng.
 */

/**
 * SỐ Ô CẤP MỎ TRONG SAVE — **3**, và con số này ĐÓNG BĂNG theo ĐỊNH DẠNG SAVE (xem chú thích đầu file).
 *
 * ⚠️ ĐỪNG XOÁ NÓ THEO `land`. `land` đã biến mất khỏi sim, nhưng mảng `mineLevels` trong save của
 * người chơi ĐANG CHƠI vẫn có đúng 3 phần tử, và hàm đọc save dưới đây (`readMeta`) dùng chính con số
 * này để cắt/ đệm mảng. Xoá là save cũ hỏng.
 *
 * ⚠️ Khai lại ở đây KHÔNG phải "nguồn sự thật thứ hai" theo nghĩa xấu: file này là tầng SAVE, nó
 * phải mô tả ĐỊNH DẠNG ĐÃ GHI RA ĐĨA chứ không được đổi theo sim. Sim đổi bố cục sân bao nhiêu lần
 * cũng không được kéo con số này theo ⇒ hai bên CỐ Ý độc lập.
 */
export const ARENA_PROGRESS_LANDS = 3;

/** Khai sinh (cấp nâng cấp) tối đa của 1 mỏ — trần cứng, xem `farmersPerLevel`. */
export const ARENA_MINE_MAX_LEVEL = 10;

/**
 * SỐ NÔNG DÂN TỐI ĐA MỖI MỎ THEO CẤP NÂNG.
 *
 * `1` ở cấp 0 để khớp đúng trạng thái đầu trận mà sim đang dựng (`ARENA_START_FARMERS = 1` —
 * "số nông dân có sẵn lúc mở màn MỖI PHE"). Cấp `n` cho thêm 1 suất.
 */
export function farmersPerLevel(level: number): number {
  return 1 + clampLevel(level);
}

/** Kẹp cấp nâng về `0..ARENA_MINE_MAX_LEVEL`, chịu được mọi giá trị rác (NaN/ âm/ chuỗi/ null). */
export function clampLevel(level: unknown): number {
  const n = typeof level === 'number' && Number.isFinite(level) ? Math.floor(level) : 0;
  return Math.min(ARENA_MINE_MAX_LEVEL, Math.max(0, n));
}

/**
 * KẾT QUẢ MỘT TRẬN ARENA — LƯU VĨNH VIỄN, nhưng CHỈ LÀ CON SỐ TỔNG KẾT.
 *
 * ⚠️ CỐ Ý KHÔNG lưu: đơn vị đang chạy, tài nguyên giữa trận, vị trí land đang chọn, đồng hồ trận.
 * Trận là thứ TẠM THỜI — `ArenaWorld` dựng lại mỗi lần vào (xem `ArenaBattleScene.attachWorld`).
 * Ghi trạng thái giữa trận vào save là mở đường cho "save hỏng ⇒ không load lại được trận", và
 * biến `migrateMeta()` thành hàm phải hiểu luật mô phỏng.
 */
export interface ArenaProgress {
  /**
   * TÀI NGUYÊN ARENA — **VÍ RIÊNG**, KHÔNG BAO GIỜ trộn vào `meta.gold`.
   *
   * Vì sao tách ví (xem `spendFromRunWallet()` trong `src/game.ts`): repo đã cố ý tách "ví trận" và
   * "ví tổng" của game TD vì người dùng yêu cầu *"trừ VÍ TRẬN khi đang đánh, trừ VÍ TỔNG khi ở
   * trang chủ"*. Arena là chế độ riêng ⇒ tiền của nó cũng phải là ví riêng; nhập chung vào
   * `meta.gold` là biến Arena thành máy in vàng cho game TD và phá cân bằng kinh tế TD đang chạy ổn.
   *
   * Ở bản này, tài nguyên TRONG TRẬN vẫn do `ArenaWorld.resource` giữ (nguồn sự thật của trận);
   * trường ở đây là KHO VĨNH VIỄN giữa các trận (thưởng sau trận), và `addArenaResource()` là cửa
   * duy nhất ghi vào nó. Trận mới vẫn khởi đầu bằng `ARENA_ECON.start` như luật của sim.
   */
  resource: number;
  /** CẤP KHAI SINH TỪNG MỎ (0..`ARENA_MINE_MAX_LEVEL`) — 3 phần tử, đúng thứ tự land 0/1/2. */
  mineLevels: number[];
  /** lớp nhân vật NHÀ MÌNH đã mở vĩnh viễn cho Arena (id `ClassId` HỢP LỆ — xem `sanitizeArenaClassIds`) */
  unlockedHeroes: string[];
  /** số trận đã THẮNG / đã THUA / HOÀ — tổng kết, không phải trạng thái trận */
  wins: number;
  losses: number;
  draws: number;
  /** trận hay nhất: thời gian sống lâu nhất (giây) */
  bestSeconds: number;
}

/**
 * STATE ARENA MỚI TINH — người chơi lần đầu, HOẶC save cũ thiếu trường, HOẶC save rác.
 *
 * ⚠️ Trả OBJECT MỚI mỗi lần gọi (không dùng chung một hằng): `sanitizeArenaProgress()` gọi hàm này
 * rồi có thể bị nơi gọi sửa (`addArenaResource` ghi thẳng vào `resource`). Dùng chung hằng số là
 * mọi save trong phiên cùng trỏ về một object ⇒ sửa trận này là sửa cả trận kia, và tệ hơn: lần
 * `migrateMeta()` sau đọc lại thấy "mặc định" đã bị bẩn.
 */
export function createArenaProgress(): ArenaProgress {
  return {
    resource: 0,
    mineLevels: Array.from({ length: ARENA_PROGRESS_LANDS }, () => 0),
    unlockedHeroes: [],
    wins: 0,
    losses: 0,
    draws: 0,
    bestSeconds: 0,
  };
}

/**
 * CÁC CLASS ĐƯỢC PHÉP RA QUÂN Ở ARENA — suy từ tiến trình TD, có thể MỞ RỘNG vĩnh viễn.
 *
 * ⚠️⚠️ VÌ SAO PHẢI SUY TỪ `meta.unlocked` CHỨ KHÔNG ĐỂ HUD CHỈ ĐỌC `arena.unlockedHeroes`:
 * `arena.unlockedHeroes` mặc định là MẢNG RỖNG, nên nếu HUD chỉ đọc nó thì người chơi MỚI mở Arena
 * sẽ thấy **KHÔNG CÓ NÚT TƯỚNG NÀO** — vào một chế độ đánh nhau mà không mua được quân. Đây là lỗi
 * đã ĐO ĐƯỢC trong Chrome thật (script `tools/verify-arena-task5.mjs` timeout ở
 * `#arenaHud [data-summon]`), không phải phòng đoán.
 *
 * Luật đúng — nhất quán với cách repo đã làm cho game TD:
 *   · CLASS MỞ Ở TD (`meta.unlocked`) ⇒ ra quân được ở Arena luôn. Người chơi đã cày 30 mảnh để mở
 *     `carpenter` thì bắt họ cày lại từ đầu ở Arena là phủ nhận công sức đó — đúng nguyên tắc
 *     "KHÔNG ĐƯỢC MẤT TIẾN TRÌNH" của repo, chỉ khác là áp cho QUYỀN CHƠI thay vì cho số liệu.
 *   · `arena.unlockedHeroes` là phần MỞ THÊM RIÊNG của Arena (thưởng/ mở khoá trong chế độ này).
 *     Kết quả là HỢP của hai nguồn; `meta.unlocked` đứng trước để thứ tự nút ỔN ĐỊNH giữa các phiên
 *     (nút nhảy chỗ giữa hai lần vào là lỗi khó chịu mà rất dễ tạo ra bằng cách đổi thứ tự nguồn).
 *
 * ⚠️ CỐ Ý KHÔNG GHI kết quả hợp này trở lại `arena.unlockedHeroes`: đó là sao chép `meta.unlocked`
 * vào save Arena, và hai bản sao lệch nhau ngay lần đầu người chơi mở class mới ở TD (bản Arena vẫn
 * thiếu ⇒ nút biến mất dù class đã mở). Nguồn sự thật vẫn là `meta.unlocked`; hàm này chỉ ĐỌC.
 *
 * ⚠️ Lọc bằng `validClasses` ở CẢ HAI vế: `meta.unlocked` cũng có thể chứa id lạ từ save rác.
 */
export function arenaHeroClasses(
  progress: ArenaProgress | undefined,
  tdUnlocked: readonly string[] | undefined,
  validClasses: readonly string[],
): string[] {
  const fromTd = sanitizeArenaClassIds(tdUnlocked, validClasses);
  const fromArena = sanitizeArenaClassIds(progress?.unlockedHeroes, validClasses);
  const seen = new Set(fromTd);
  const out = [...fromTd];
  for (const id of fromArena) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Số hữu hạn ≥ 0, đã làm tròn xuống. Mọi giá trị khác (NaN/ Infinity/ âm/ chuỗi/ null) ⇒ 0. */
function safeCount(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? Math.floor(v) : 0;
  return n > 0 ? n : 0;
}

/**
 * LỌC DANH SÁCH CLASS CỦA ARENA: CHỈ giữ id NẰM TRONG `valid`.
 *
 * ⚠️ VÌ SAO LỌC Ở ĐÂY CHỨ KHÔNG Ở UI: danh sách này chảy thẳng vào `ArenaWorld.settings.heroClasses`
 * (validate lệnh summon) và vào nút HUD. Save rác có `unlockedHeroes: ['khongCoClass', null, 42]`
 * mà không lọc thì HUD dựng nút gọi `CLASSES['khongCoClass'].name` ⇒ ném `TypeError` NGAY LÚC MỞ
 * MÀN HÌNH, tức save rác làm hỏng luôn cả game TD (vì `migrateMeta()` chạy lúc boot).
 *
 * ⚠️ Nguồn "class hợp lệ" được TRUYỀN VÀO (không import `data/classes.ts`): nhờ vậy hàm là THUẦN
 * TUÝ và test dựng được cả hai vế (id hợp lệ/ id rác) mà không phụ thuộc danh sách class thật.
 */
export function sanitizeArenaClassIds(value: unknown, valid: readonly string[]): string[] {
  if (!Array.isArray(value)) return [];
  const allowed = new Set(valid);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of value) {
    if (typeof id !== 'string' || !allowed.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * CỨU MỘT OBJECT TIẾN TRÌNH ARENA TỪ SAVE (CŨ/ THIẾU/ RÁC) ⇒ LUÔN RA TIẾN TRÌNH HỢP LỆ.
 *
 * HÀM NÀY LÀ CỬA DUY NHẤT để `migrateMeta()` vá trường `arena`, và nó KHÔNG BAO GIỜ NÉM:
 *   · `raw === undefined`/ `null`/ chuỗi/ số/ mảng  ⇒ toàn bộ mặc định (đây là đường của save v7).
 *   · `resource` sai kiểu/ NaN/ âm                  ⇒ 0.
 *   · `mineLevels` không phải mảng, ngắn, dài, chứa rác ⇒ ĐÚNG `ARENA_PROGRESS_LANDS` phần tử 0..trần.
 *   · `unlockedHeroes` chứa id lạ/ null/ trùng      ⇒ bị loại, giữ thứ tự xuất hiện.
 *   · `wins`/`losses`/`draws`/`bestSeconds` rác     ⇒ 0.
 *
 * ⚠️ MỌI TRƯỜNG ĐỀU ĐƯỢC GÁN TƯỜNG MINH (không `{...defaults, ...raw}`): spread thô sẽ mang theo
 * MỌI khoá rác của save cũ (kể cả khoá của phiên bản sau này) vào state sống, rồi `saveMeta()` ghi
 * chúng trở lại đĩa — đúng "rác vĩnh viễn" mà `migrateMeta()` đã phải `delete` cho `heroes`/
 * `ownedAccessories` (xem chú thích ở đó).
 */
export function sanitizeArenaProgress(raw: unknown, validClasses: readonly string[]): ArenaProgress {
  const base = createArenaProgress();
  const src = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;

  const rawLevels = Array.isArray(src.mineLevels) ? src.mineLevels : [];
  const mineLevels = Array.from({ length: ARENA_PROGRESS_LANDS }, (_, i) => clampLevel(rawLevels[i]));

  return {
    resource: safeCount(src.resource),
    mineLevels,
    unlockedHeroes: sanitizeArenaClassIds(src.unlockedHeroes, validClasses),
    wins: safeCount(src.wins),
    losses: safeCount(src.losses),
    draws: safeCount(src.draws),
    bestSeconds: safeCount(src.bestSeconds),
  };
}

/**
 * CỘNG TÀI NGUYÊN VÀO VÍ ARENA — **CỬA DUY NHẤT** ghi vào `arena.resource`.
 *
 * ⚠️ VÌ SAO CÓ HÀM NÀY THAY VÌ `p.arena.resource += n` RẢI KHẮP NƠI: mọi chỗ tự cộng là mọi chỗ có
 * thể quên kẹp (cộng số âm = trừ tiền, cộng `NaN` = ví chết vĩnh viễn vì `NaN` lan qua mọi phép
 * toán sau đó, và `JSON.stringify(NaN)` ghi ra `null` ⇒ lần load sau thành 0, im lặng).
 *
 * ⚠️ VÀ TUYỆT ĐỐI KHÔNG CHẠM `meta.gold`: tài nguyên Arena là VÍ RIÊNG (xem chú thích
 * `ArenaProgress.resource`). Trộn hai ví là phá luật kinh tế của cả hai chế độ.
 *
 * @returns số thực sự được cộng (0 nếu `n` không phải số hữu hạn dương)
 */
export function addArenaResource(progress: ArenaProgress, n: number): number {
  const delta = typeof n === 'number' && Number.isFinite(n) ? Math.floor(n) : 0;
  if (delta <= 0) return 0;
  progress.resource = safeCount(progress.resource) + delta;
  return delta;
}

/** Ví Arena còn đủ `cost` không. Dùng chung một chỗ để UI và luật không lệch nhau. */
export function canAffordArena(progress: ArenaProgress, cost: number): boolean {
  return safeCount(progress.resource) >= safeCount(cost);
}

/**
 * NÂNG CẤP MỘT MỎ (khai sinh) — TRẢ VỀ KẾT QUẢ, KHÔNG BAO GIỜ NÉM.
 *
 * `land` sai (ngoài 0..2) ⇒ `ok:false` chứ KHÔNG ghi vào ô `undefined` của mảng (ghi vào
 * `mineLevels[7]` là tạo phần tử rỗng, và `JSON.stringify` biến mảng thành `[0,0,0,null,null,…]`).
 */
export function upgradeArenaMine(
  progress: ArenaProgress,
  land: number,
  cost: number,
): { ok: boolean; message: string } {
  const idx = Number.isInteger(land) ? land : -1;
  if (idx < 0 || idx >= ARENA_PROGRESS_LANDS) return { ok: false, message: 'Land không hợp lệ' };
  const level = clampLevel(progress.mineLevels[idx]);
  if (level >= ARENA_MINE_MAX_LEVEL) return { ok: false, message: `Mỏ đã đạt cấp tối đa ${ARENA_MINE_MAX_LEVEL}` };
  const price = safeCount(cost);
  if (safeCount(progress.resource) < price) {
    return { ok: false, message: `Không đủ tài nguyên Arena (cần ${price})` };
  }
  progress.resource -= price;
  progress.mineLevels[idx] = level + 1;
  return { ok: true, message: `Mỏ cấp ${level + 1}` };
}

/**
 * GHI KẾT QUẢ MỘT TRẬN VÀO TIẾN TRÌNH.
 *
 * ⚠️ CHỈ nhận `'won' | 'lost' | 'draw'` — KHÔNG nhận `'prep'`/ `'fighting'`. Lý do: hàm này chạy
 * trong `onStatusChange` của scene, mà trận ĐANG ĐÁNH cũng phát trạng thái. Nhận trạng thái giữa
 * trận là mỗi lần người chơi tạm dừng/ vào trận lại là cộng thêm một "trận thua" vào save — sai
 * tiến trình một cách âm thầm. Trạng thái lạ ⇒ bỏ qua, KHÔNG ném.
 *
 * @returns `true` nếu có ghi (trận vừa kết thúc), `false` nếu bỏ qua
 */
export function recordArenaResult(progress: ArenaProgress, status: string, seconds: number): boolean {
  if (status !== 'won' && status !== 'lost' && status !== 'draw') return false;
  if (status === 'won') progress.wins = safeCount(progress.wins) + 1;
  else if (status === 'lost') progress.losses = safeCount(progress.losses) + 1;
  else progress.draws = safeCount(progress.draws) + 1;
  const t = safeCount(seconds);
  if (t > safeCount(progress.bestSeconds)) progress.bestSeconds = t;
  return true;
}

/** Tổng số trận đã đánh (thắng + thua + hoà) — HUD đọc để hiện tỉ lệ thắng. */
export function arenaMatchCount(progress: ArenaProgress): number {
  return safeCount(progress.wins) + safeCount(progress.losses) + safeCount(progress.draws);
}
